import { castToSnapshot, destroy, flow, Instance, SnapshotIn, types, getRoot } from 'mobx-state-tree';
import { callAgroApi } from '@services/agroapi';
import { Activity } from './Activity';
import { Farm, IFarm } from './Farm';
import { FollowStore } from './FollowStore';
import { DateTime } from 'luxon';
import { PlaceStore } from './PlaceStore';
import { ITransactions, TxStore } from './TransactionStore';
import { IActivityItem } from '@app/activity/_components/dnd';
import { LabelStore } from './LabelStore';
import { SeasonStore } from './SeasonStore';
import { Field, IFieldSnapshotIn } from './Field';
import { Cropzone, ICropzone } from './Cropzone';
import { isPresent } from '@farmai-shared/LFUtils';
import { removeEmptyExtra } from '@services/utils';
import { withNotifications } from './extensions/withNotifications';
import { withLabels } from './extensions/withLabels';
import { withSeasons } from './extensions/withSeasons';
import { withAssignments } from './extensions/withAssignments';
import { withPlaces } from './extensions/withPlaces';
import { withTemplates } from './extensions/withTemplates';
import { withTransactions } from './extensions/withTransactions';
import { withActivities } from './extensions/withActivities';
import { withSupplyChain } from './extensions/withSupplyChain';
import { OrganizationStats } from './OrganizationStats';
import { CropSummary } from './CropSummary';
import { ActivityResponse, AdministrativeLevelsResponse, AssignmentResponse, AssignmentsResponse, BoardGroupResponse, BoardsResponse, CountriesResponse, CropzoneGeoJSON, CropzoneResponse, FarmResponse, FieldResponse, FollowResponse, FollowsResponse, OrganizationImportQueuedResponse, OrganizationImportResponse, OrganizationImportStatus, OrganizationResponse, PlantingResponse, QrType, RegionResponse, SankeyResponse, SeasonResponse, StatResponse, TemplateResponse, TemplatesResponse, TransactionResponse, UserResponse, YieldStatsResponse } from '@services/responseTypes';
import { Transformers } from '@services/transformer';
import { IRootStoreAny } from './rootUtilType';
import { BasicOrganization } from './BasicOrganization';
import { CropzoneFormDataWithTraceability } from '@app/_components/common/CropzoneInputs/utils';
import { ITemplate, Template } from './Template';
const archiveOldForField = (orgId: string, fieldId: string, keepCropzoneId: string): Promise<void> => callAgroApi<CropzoneResponse[]>(`/organizations/${orgId}/cropzones?items=100&page=1&filter[pending]=false&filter[status]=current&filter[field_id]=${fieldId}`, 'GET').then(({
  data
}) => Promise.allSettled(data.filter(cz => cz.id !== keepCropzoneId && !cz.archived_at).map(cz => callAgroApi<{
  message: 'success';
}>(`/cropzones/${cz.id}/archive`, 'POST')))).then(() => undefined);
const IMPORT_POLL_INTERVAL_MS = 3000;
const IMPORT_POLL_MAX_DELAY_MS = 15000;
const IMPORT_POLL_MAX_FAILURES = 10;
const IMPORT_STILL_PROCESSING = 'ImportStillProcessingError';
const IMPORT_TERMINAL_STATUSES: OrganizationImportStatus[] = ['imported', 'partial', 'failed'];
type SyncImportResponse = {
  fields: number;
  cropzones: number;
  soil_samples: number;
  activities: number;
  sheets: number;
  files: string[];
  errors: {
    file: string;
    error: string;
  }[];
};
const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));
const isTransientError = (error: any) => {
  const status = error?.response?.status;
  return status === undefined || status === 429 || status >= 500;
};
const importStillProcessingError = (importId: string | null) => Object.assign(new Error(IMPORT_STILL_PROCESSING), {
  name: IMPORT_STILL_PROCESSING,
  importId
});
export const isImportStillProcessing = (error: any) => error?.name === IMPORT_STILL_PROCESSING;
const importPollDelay = (failures: number) => Math.min(IMPORT_POLL_MAX_DELAY_MS, IMPORT_POLL_INTERVAL_MS * 2 ** Math.max(0, failures - 1));
const fetchOrganizationImport = (orgId: string, importId: string) => callAgroApi<OrganizationImportResponse>(`/organizations/${orgId}/imports/${importId}`, 'GET').then(({
  data
}) => data);
const syncImportToResponse = (data: SyncImportResponse, file: File): OrganizationImportResponse => ({
  id: '',
  filename: file.name,
  status: data.errors.length ? 'partial' : 'imported',
  stats: {
    fields: data.fields,
    cropzones: data.cropzones,
    soil_samples: data.soil_samples,
    activities: data.activities,
    sheets: data.sheets
  },
  warnings: [],
  errors: data.errors,
  byte_size: file.size,
  uploaded_at: new Date().toISOString(),
  uploaded_by: null
});
const autoArchiveAllForVsc = async (orgId: string): Promise<void> => {
  const ITEMS = 200;
  let page = 1;
  const fieldMap = new Map<string, {
    id: string;
    plantingDate: string | null;
    createdAt: string;
  }[]>();
  while (true) {
    const {
      data
    } = await callAgroApi<CropzoneResponse[]>(`/organizations/${orgId}/cropzones?items=${ITEMS}&page=${page}&filter[pending]=false&filter[status]=current`, 'GET');
    if (!data.length) break;
    for (const cz of data) {
      const fieldId = cz.field?.id;
      if (cz.archived_at || !fieldId) continue;
      const bucket = fieldMap.get(fieldId) ?? [];
      bucket.push({
        id: cz.id,
        plantingDate: cz.planting_date,
        createdAt: cz.created_at
      });
      fieldMap.set(fieldId, bucket);
    }
    if (data.length < ITEMS) break;
    page++;
  }
  const toArchive: string[] = [];
  fieldMap.forEach(czs => {
    if (czs.length <= 1) return;
    czs.sort((a, b) => {
      const da = a.plantingDate ?? a.createdAt;
      const db = b.plantingDate ?? b.createdAt;
      return da > db ? -1 : da < db ? 1 : 0;
    });
    czs.slice(1).forEach(cz => toArchive.push(cz.id));
  });
  await Promise.allSettled(toArchive.map(id => callAgroApi<{
    message: 'success';
  }>(`/cropzones/${id}/archive`, 'POST')));
};
export const Organization = BasicOrganization.named('Cropzone').props({
  avatar: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  address: types.maybeNull(types.string),
  prodTargets: types.model({
    dailyMin: types.number,
    dailyMax: types.number
  }),
  seed_usage_unit: types.maybeNull(types.string),
  farms: types.map(types.late(() => Farm)),
  followStore: types.optional(types.late(() => FollowStore), () => FollowStore.create({
    completed: {
      follows: {}
    },
    pending: {
      follows: {},
      followers: {}
    }
  })),
  templates: types.map(types.late(() => Template)),
  stats: types.maybeNull(types.late(() => OrganizationStats)),
  activities: types.map(types.late(() => Activity)),
  cropSummary: types.array(types.late(() => CropSummary)),
  createdAt: types.maybeNull(types.number),
  cropzones: types.map(types.late(() => Cropzone)),
  fields: types.map(types.late(() => Field)),
  features: types.map(types.boolean),
  placeStore: types.optional(types.late(() => PlaceStore), () => PlaceStore.create({})),
  txStore: types.optional(types.late(() => TxStore), () => TxStore.create({})),
  labelStore: types.optional(types.late(() => LabelStore), () => LabelStore.create({})),
  seasonStore: types.optional(types.late(() => SeasonStore), () => SeasonStore.create({}))
}).views(self => ({
  get root() {
    return getRoot<IRootStoreAny>(self);
  }
})).views(self => ({
  get yearList() {
    if (self.createdAt) {
      const lang = self.root.userStore.user.settings.lang;
      const range = [...Array(DateTime.now().year - self.createdAt + 2).keys()].map(i => i + self.createdAt!);
      return range.map(year => ({
        label: (lang === 'th' ? year + 543 : year).toString(),
        value: year.toString()
      })).reverse();
    }
  },
  get hasTraceabilityFeature() {
    return self.features.get('traceability') === true;
  },
  get hasPlanningFeature() {
    return self.features.get('planning') === true;
  },
  get hasSoilAnalysisFeature() {
    return self.features.get('soil_analysis') === true;
  },
  get hasPotatoCoverFeature() {
    return self.features.get('potato_cover') === true;
  },
  get hasPlanetSupport() {
    return self.features.get('planet') === true;
  },
  get hasSupplyChainFeature() {
    return self.features.get('supply_chain') === true;
  }
})).actions(self => ({
  pushToActivities(activities) {
    return activities.map(a => self.activities.put(a));
  },
  setName(newName: string) {
    self.name = newName;
  },
  setAddress(newAddress: string) {
    self.address = newAddress;
  },
  setPhone(newPhone: string) {
    self.phone = `+${newPhone.replace(/\+/g, '')}`;
  },
  setSeedUsageUnit(newSeedUnit: string) {
    self.seed_usage_unit = newSeedUnit;
  },
  setMaxProduction(production: string) {
    self.prodTargets.dailyMax = parseFloat(production);
  },
  setMinProduction(production: string) {
    self.prodTargets.dailyMin = parseFloat(production);
  },
  pushToFarms(farm: FarmResponse) {
    const snapshot = castToSnapshot(Transformers.farm(farm));
    const farmInstance = self.farms.put(snapshot);
    return farmInstance;
  },
  removeFromFarms(farm: IFarm) {
    destroy(farm);
  },
  formatQueryDates(year: number, month?: number) {
    return {
      from: encodeURIComponent(DateTime.fromObject({
        year: year,
        month: month || 1,
        day: 1
      }).toISO()),
      to: encodeURIComponent(DateTime.fromObject({
        year: year,
        month: month || 12,
        day: 28
      }).endOf('month').toISO())
    };
  },
  pushToNewCropzones(cropzone: CropzoneGeoJSON) {
    const staleCropzone: ICropzone | undefined = self.cropzones.get(cropzone.id);
    self.cropzones.put(castToSnapshot({
      ...cropzone,
      crop: cropzone.crop.id,
      season: isPresent(cropzone.season) ? cropzone.season.id : staleCropzone?.season?.id,
      labels: isPresent(cropzone.labels) ? cropzone.labels.map(l => l.id) : staleCropzone?.labels || []
    }));
  },
  formatRecentMonthDates(date: DateTime) {
    return {
      from: encodeURIComponent(date.minus({
        months: 1
      }).startOf('day').toISO()),
      to: encodeURIComponent(date.endOf('day').toISO())
    };
  },
  pushToCompletedFollow(follow) {
    self.followStore.completed.follows.put(follow);
    return self.followStore.completed.follows.get(follow.id);
  },
  replaceCompletedFollows(follows) {
    self.followStore.completed.follows.replace(new Map(follows.map(f => [f.id, f])));
    return follows.map(f => self.followStore.completed.follows.get(f.id));
  },
  destroyCropzone(cropzone: ICropzone) {
    destroy(cropzone);
  },
  pushToFields(field: IFieldSnapshotIn) {
    self.fields.put(field);
    return self.fields.get(field.id);
  }
})).actions(self => ({
  handleNewCropzones(cropzones: CropzoneGeoJSON[]) {
    const placeIds = new Set<string>([]);
    const newCropzonesToPush = new Set<CropzoneGeoJSON>([]);
    cropzones.map(cropzone => {
      cropzone.traceability?.harvest_destination_places?.forEach((pId: string) => {
        if (!self.placeStore.places.has(pId)) {
          placeIds.add(pId);
        }
      });
      if (!self.root.cropStore.cropCache[cropzone.crop.id]) {
        self.root.cropStore.pushToCrops(cropzone.crop);
      }
      if (isPresent(cropzone.season)) {
        self.seasonStore.pushToSeasons(cropzone.season);
      }
      cropzone.labels.forEach(l => {
        if (l && !self.labelStore.labels.has(l.id)) {
          self.labelStore.pushToLabels(l);
        }
      });
      newCropzonesToPush.add(cropzone);
    });
    return Promise.all(Array.from(placeIds).map(id => self.placeStore.fetchPlace(id))).then(() => {
      newCropzonesToPush.forEach(cropzone => self.pushToNewCropzones(cropzone));
      return cropzones;
    });
  },
  storeCropzonesFromActivities(activityObjects: ReturnType<typeof Transformers.activity>[]) {
    return this.handleNewCropzones(activityObjects.map(x => x.cropzone!)).then(() => Promise.resolve(self.pushToActivities(activityObjects.map(a => {
      if (!self.root.activityTypeStore.actTypeIds[a.activityType.id]) {
        self.root.activityTypeStore.pushActivityType(a.activityType);
      }
      return {
        ...a,
        cropzone: a.cropzone?.properties.id,
        activityType: a.activityType.id
      };
    }))));
  },
  destroyActivity(activity) {
    destroy(activity);
  }
})).actions(self => ({
  fetchOrgStats() {
    return callAgroApi<OrganizationResponse>(`/organizations/${self.id}`, 'GET').then(response => Transformers.organization(response.data));
  }
})).actions(self => ({
  fetchFollows(params: {
    search?: string;
    page?: number;
    farmId?: string;
  }) {
    const {
      search,
      page = 1,
      farmId
    } = params;
    return callAgroApi<FollowsResponse>(`/organizations/${self.id}/follows?${farmId ? `filter[farm_id]=${farmId}&` : ''}page=${page}&${search ? `filter[query]=${search}` : ''}`, 'GET').then(({
      data
    }) => {
      if (!isPresent(data.follows)) return [];
      return data.follows.map(f => Transformers.follow(f));
    }).then(follows => follows.map(self.pushToCompletedFollow));
  },
  fetchTemplates(params: {
    search?: string;
    page?: number;
  }): Promise<ITemplate[]> {
    const {
      search,
      page = 1
    } = params;
    const url = `/organizations/${self.id}/templates?page=${page}&${search ? `filter[query]=${search}` : ''}`;
    return callAgroApi<TemplatesResponse>(url, 'GET').then(({
      data
    }) => {
      if (!isPresent(data)) return [];
      return data.map(t => Transformers.template(t));
    });
  },
  fetchTemplate(templateId: string) {
    const url = `/templates/${`${templateId}`}`;
    return callAgroApi<TemplateResponse>(url, 'GET').then(({
      data
    }) => {
      if (!isPresent(data)) return null;
      return Transformers.template(data);
    });
  },
  fetchAssignments(params: {
    search?: string;
    page?: number;
  }) {
    const {
      search,
      page = 1
    } = params;
    const url = `/organizations/${self.id}/template_assignments?page=${page}&${search ? `filter[query]=${search}` : ''}`;
    return callAgroApi<AssignmentsResponse>(url, 'GET').then(({
      data
    }) => {
      if (!isPresent(data)) return [];
      return data.map(t => Transformers.assignment(t));
    });
  },
  fetchAssignment(assignmentId: string) {
    const url = `/template_assignments/${`${assignmentId}`}`;
    return callAgroApi<AssignmentResponse>(url, 'GET').then(({
      data
    }) => {
      if (!isPresent(data)) return null;
      return Transformers.assignment(data);
    });
  },
  fetchPendingFollows({
    page = 1
  } = {}) {
    const url = `/organizations/${self.id}/follows?filter[status]=pending${page ? `&page=${page}` : ''}`;
    return callAgroApi<FollowsResponse>(url, 'GET').then(({
      data
    }) => {
      if (!isPresent(data.follows)) return [];
      return data.follows.map(f => Transformers.follow(f, 'organization'));
    });
  },
  fetchPendingFollowers({
    page = 1
  } = {}) {
    const url = `/organizations/${self.id}/followers?filter[status]=pending${page ? `&page=${page}` : ''}`;
    return callAgroApi<FollowsResponse>(url, 'GET').then(({
      data
    }) => {
      if (!isPresent(data.follows)) return [];
      return data.follows.map(f => Transformers.follow(f, 'farmer'));
    });
  },
  fetchCountryWithMostFields() {
    return self.fetchOrgStats().then(orgData => {
      if (orgData.stats?.totalFieldsPerCountry && Object.keys(orgData.stats.totalFieldsPerCountry).length > 0) {
        const sortedEntries = Object.entries(orgData.stats.totalFieldsPerCountry).sort(([, a], [, b]) => b - a); // Sort by field count descending

        return sortedEntries[0][0];
      }
      return null;
    });
  },
  clearActivities() {
    self.activities.clear();
  },
  clearCropzones() {
    self.cropzones.clear();
  },
  addFarm(farm: {
    name: string;
    phone?: string;
    address: string | undefined;
  }) {
    return callAgroApi<FarmResponse>(`/organizations/${self.id}/farms`, 'POST', farm).then(({
      data
    }) => {
      self.pushToFarms(data);
      return data;
    });
  },
  fetchFarms({
    search,
    page,
    pointLocation
  }: {
    search?: string;
    page?: number;
    pointLocation?: [number, number];
  }) {
    let queryString = '';
    if (search) {
      queryString += `filter[query]=${search}&`;
    }
    if (isPresent(pointLocation)) {
      queryString += `sort_by=distance&location=${pointLocation.toString()}&`;
    }
    return callAgroApi<FarmResponse[]>(`/organizations/${self.id}/farms?${queryString}page=${page?.toString() || '1'}`, 'GET').then(({
      data
    }) => data.map(farm => self.pushToFarms(farm)));
  },
  fetchFarm(id: string) {
    return callAgroApi<FarmResponse>(`/farms/${id}`, 'GET').then(({
      data
    }) => self.pushToFarms(data));
  },
  inviteFarmer(slug: {
    slug: string;
  }) {
    return callAgroApi<FollowResponse>(`/organizations/${self.id}/follow`, 'POST', {
      user: slug
    });
  },
  fetchYieldStats({
    season,
    year = DateTime.now().year,
    crop,
    district,
    village,
    region,
    country,
    province,
    subdistrict,
    labels,
    interval,
    from,
    to
  }: {
    season?: string | null;
    year?: number | null;
    crop?: string | null;
    labels?: string | null;
    village?: string;
    country?: string;
    district?: string;
    region?: string;
    province?: string;
    subdistrict?: string;
    interval?: 'day' | 'week' | 'month';
    from?: string | null;
    to?: string | null;
  }) {
    let queryString;
    const labelParams = labels?.split(',').map(labelId => `filter[labels][]=${labelId}`);
    if (season) {
      queryString = [`tz=${DateTime.local().zoneName}`, season ? `filter[season_id]=${season}` : null, crop ? `crop=${crop}` : null, country ? `filter[country]=${country}` : null, district ? `filter[district]=${district}` : null, region ? `filter[region]=${region}` : null, province ? `filter[province]=${province}` : null, village ? `filter[village]=${village}` : null, subdistrict ? `filter[subdistrict]=${subdistrict}` : null, interval ? `interval=${interval}` : null, ...(labelParams || [])].filter(Boolean).join('&');
    } else {
      const dates = from && to ? {
        from,
        to
      } : self.formatQueryDates(year!);
      queryString = [`tz=${DateTime.local().zoneName}`, dates.from ? `from=${dates.from}` : null, dates.to ? `to=${dates.to}` : null, crop ? `crop=${crop}` : null, country ? `filter[country]=${country}` : null, district ? `filter[district]=${district}` : null, region ? `filter[region]=${region}` : null, province ? `filter[province]=${province}` : null, village ? `filter[village]=${village}` : null, subdistrict ? `filter[subdistrict]=${subdistrict}` : null, interval ? `interval=${interval}` : null, ...(labelParams || [])].filter(Boolean).join('&');
    }
    return callAgroApi<YieldStatsResponse>(`/organizations/${self.id}/stats/yield?${queryString}`, 'GET').then(({
      data
    }) => Transformers.yieldStats(data));
  },
  updateStats(statData: {
    stat: {
      target: number;
      id: string;
    };
  }) {
    return callAgroApi<YieldStatsResponse>(`/stats/${statData.stat.id}`, 'PATCH', statData).then(({
      data
    }) => Transformers.yieldStats(data));
  },
  fetchRegions() {
    return callAgroApi<RegionResponse>(`/organizations/${self.id}/regions`, 'GET');
  },
  fetchCountries() {
    return callAgroApi<CountriesResponse>(`/organizations/${self.id}/countries`, 'GET');
  },
  fetchAdministrativeLevels({
    page,
    region,
    country,
    district,
    subdistrict
  }: {
    page?: number;
    region?: string;
    district?: string;
    country?: string;
    subdistrict?: string;
  } = {}) {
    let queryString = '';
    if (country) {
      queryString += `filter[country]=${country}&`;
    }
    if (region) {
      queryString += `filter[region]=${region}&`;
    }
    if (district) {
      queryString += `filter[district]=${district}&`;
    }
    if (subdistrict) {
      queryString += `filter[subdistrict]=${subdistrict}&`;
    }
    return callAgroApi<AdministrativeLevelsResponse>(`/organizations/${self.id}/administrative_levels?${queryString}page=${page?.toString() || '1'}`, 'GET');
  },
  fetchCropzones({
    page = 1,
    query,
    bbox,
    farmId,
    fieldId,
    cropProgress,
    cropName,
    zoom,
    distance,
    end_date_from,
    end_date_to,
    start_date_from,
    start_date_to,
    pending = false,
    sortBy,
    location,
    status,
    cropId,
    labels,
    includesVi = false,
    seasonId,
    items = 20
  }: {
    page?: number;
    query?: string;
    bbox?: number[][];
    farmId?: string | null;
    fieldId?: string;
    cropProgress?: string;
    cropName?: string;
    pending?: boolean;
    zoom?: number;
    distance?: number;
    end_date_from?: string;
    end_date_to?: string;
    start_date_from?: string;
    start_date_to?: string;
    sortBy?: string;
    location?: string | null;
    status?: string;
    cropId?: string;
    labels?: string[];
    includesVi?: boolean;
    seasonId?: string;
    items?: number;
  }) {
    let queryString = `items=${items}&`;
    queryString += `filter[pending]=${pending.toString()}&`;
    if (farmId) {
      queryString += `filter[farm_id]=${farmId}&`;
    }
    if (fieldId) {
      queryString += `filter[field_id]=${fieldId}&`;
    }
    if (cropProgress) {
      queryString += `filter[crop_progress]=${cropProgress}&`;
    }
    if (query || cropName && !cropId) {
      queryString += `filter[query]=${query || cropName}&`;
    }
    if (bbox) {
      queryString += `boundary=${JSON.stringify(bbox)}&`;
    }
    if (sortBy && sortBy !== 'distance') {
      queryString += `sort_by=${sortBy}&`;
    }
    if (zoom) {
      queryString += `z=${zoom}&`;
    }
    if (distance) {
      queryString += `distance=${distance}&`;
    }
    if (sortBy === 'distance' && location) {
      queryString += `sort_by=${sortBy}&location=${location}&`;
    }
    if (end_date_from) {
      queryString += `filter[end_date_from]=${end_date_from}&`;
    }
    if (end_date_to) {
      queryString += `filter[end_date_to]=${end_date_to}&`;
    }
    if (start_date_from) {
      queryString += `filter[start_date_from]=${start_date_from}&`;
    }
    if (start_date_to) {
      queryString += `filter[start_date_to]=${start_date_to}&`;
    }
    if (seasonId) {
      queryString += `filter[season_id]=${seasonId}&`;
    }
    if (status) {
      queryString += `filter[status]=${status}&`;
    }
    if (cropId) {
      queryString += `filter[crop_id]=${cropId}&`;
    }
    if (includesVi) {
      queryString += `include=latest_vis&`;
    }
    if (labels) {
      queryString += labels.map(l => `filter[labels][]=${l}`).join('&'); // will not have a `&` at the end
    }
    return callAgroApi<CropzoneResponse[]>(`/organizations/${self.id}/cropzones?page=${page.toString()}&${queryString}`, 'GET').then(({
      data
    }): Promise<CropzoneGeoJSON[]> => {
      const cropzones = data.map(cz => Transformers.cropzone(cz));
      return self.handleNewCropzones(cropzones);
    });
  },
  fetchCropzone(id: string) {
    return callAgroApi<CropzoneResponse>(`/organizations/${self.id}/cropzones/${id}`, 'GET').then(response => Transformers.cropzone(response.data)).then(cropzone => self.handleNewCropzones([cropzone]).then(() => self.cropzones.get(cropzone.properties.id)));
  },
  fetchActivities({
    cropzoneId,
    status = 'all',
    page = 1
  }: {
    cropzoneId?: string;
    status?: 'current' | 'archived' | 'all';
    page?: number;
  }) {
    let queryString = '';
    if (cropzoneId) {
      queryString += `filter[cropzone_id]=${cropzoneId}&`;
    }
    if (status) {
      queryString += `filter[status]=${status}&`;
    }
    return callAgroApi<ActivityResponse[]>(`/organizations/${self.id}/activities?page=${page.toString()}&${queryString}`, 'GET').then(response => response.data.filter(d => d.type === 'CropzoneActivity').map(x => Transformers.activity(x))).then(activityCropzoneObjects => self.storeCropzonesFromActivities(activityCropzoneObjects).then(() => activityCropzoneObjects));
  },
  createCropzone(data: CropzoneFormDataWithTraceability & {
    field_id: string;
    pending?: boolean;
  }) {
    return callAgroApi<CropzoneResponse>(`/organizations/${self.id}/cropzones`, 'POST', {
      crop_id: data.crop.value,
      pending: data.pending || false,
      ...(isPresent(data.generation?.value) && {
        generation: data?.generation?.value
      }),
      ...(isPresent(data.plantingDate) && {
        planting_date: data.plantingDate
      }),
      field_id: data.field_id,
      seed_usage: Number(data.amountOfSeeds),
      seed_usage_unit: data.amountOfSeeds_unit?.value || 'bags',
      labels: data.labels?.map(l => ({
        id: l.value
      })),
      ...(isPresent(data.location) && {
        location: data.location
      }),
      ...((isPresent(data.production) || isPresent(data.harvestDate)) && {
        actual: {
          harvest: isPresent(data.production) ? data.production : null,
          harvesting_date: isPresent(data.harvestDate) ? data.harvestDate : null
        }
      })
    }).then(({
      data
    }) => Transformers.cropzone(data)).then(cropzone => self.handleNewCropzones([cropzone]).then(() => {
      if (self.hasSupplyChainFeature && !data.pending) {
        return archiveOldForField(self.id, data.field_id, cropzone.id).catch(() => undefined).then(() => self.cropzones.get(cropzone.properties.id));
      }
      return self.cropzones.get(cropzone.properties.id);
    }));
  },
  deleteCropzone({
    cropzoneId
  }: {
    cropzoneId: string;
  }) {
    return callAgroApi<{
      message: string;
    }>(`/cropzones/${cropzoneId}`, 'DELETE').then(({
      data
    }) => data);
  },
  deleteField({
    fieldId
  }: {
    fieldId: string;
  }) {
    return callAgroApi<{
      message: string;
    }>(`/fields/${fieldId}`, 'DELETE').then(({
      data
    }) => data);
  },
  createField({
    farmId,
    name,
    location,
    pending = false
  }: {
    farmId: string;
    name: string;
    location: {
      type: string;
      coordinates: number[][][];
    };
    pending?: boolean;
  }) {
    return callAgroApi<FieldResponse>(`/farms/${farmId}/fields`, 'POST', {
      name: name,
      location: location,
      pending: pending
    }).then(({
      data
    }) => data);
  },
  renewCropzone(cropzoneId: string) {
    return callAgroApi<CropzoneResponse>(`/cropzones/${cropzoneId}/renew`, 'POST').then(({
      data
    }) => Transformers.cropzone(data)).then(cropzone => self.handleNewCropzones([cropzone]).then(() => self.cropzones.get(cropzone.properties.id)));
  },
  fetchFields(farm_id?: string, searchQuery?: string, page = 1) {
    return callAgroApi<FieldResponse[]>(`/organizations/${self.id}/fields?farm_id=${farm_id || ''}&filter[query]=${searchQuery}&page=${page.toString()}`, 'GET').then(response => response.data.map(f => Transformers.field(f))).then(fields => fields.map(field => self.pushToFields(field)));
  },
  fetchField(fieldId: string) {
    return callAgroApi<FieldResponse>(`/fields/${fieldId}`, 'GET').then(({
      data
    }) => Transformers.field(data)).then(field => self.pushToFields(field));
  },
  fetchSeasons(page = 1) {
    return callAgroApi<SeasonResponse[]>(`/organizations/${self.id}/seasons?page=${page.toString()}`, 'GET').then(response => response.data.map(s => Transformers.season(s))).then(seasons => seasons.map(season => self.seasonStore.pushToSeasons(season)));
  },
  fetchSeason(seasonId: string) {
    return callAgroApi<SeasonResponse>(`/organizations/${self.id}/seasons/${seasonId}`, 'GET').then(({
      data
    }) => Transformers.season(data)).then(season => self.seasonStore.pushToSeasons(season));
  },
  updateCropzonesDistance(distance: number[]) {
    self.cropzones.forEach(cropzone => cropzone.updateDistance(distance));
  },
  getAllQrs: flow(function* getAllQrs(callback: (params: {
    response: QrType;
    currentCount: number;
    page: number;
    total: number;
  }) => void) {
    let page = 1;
    const perPage = 10;
    let allQrs: QrType = {};
    let isLoading = true;
    do {
      yield callAgroApi<QrType>(`/organizations/${self.id}/farms/qrs?page=${page}&items=${perPage}`, 'GET').then(response => {
        if (Object.keys(response.data).length == 0) {
          isLoading = false;
          return;
        }
        allQrs = {
          ...allQrs,
          ...response.data
        };
        page += 1;
        return callback({
          response: response.data,
          currentCount: Object.keys(allQrs).length,
          page: page,
          total: response.headers['total-count']
        });
      });
    } while (isLoading);
    return allQrs;
  }),
  save(org: {
    name: string;
    phone?: string;
    address?: string;
    prod_targets?: {
      daily_min: number;
      daily_max: number;
    };
    seed_usage_unit?: string;
  }) {
    return callAgroApi<OrganizationResponse>(`/organizations/${self.id}`, 'PATCH', org).then(response => {
      self.setName(response.data.name);
      response.data.address && self.setAddress(response.data.address);
      response.data.phone && self.setPhone(response.data.phone);
      response.data.prod_targets?.daily_min && self.setMinProduction(response.data.prod_targets.daily_min);
      response.data.prod_targets?.daily_max && self.setMaxProduction(response.data.prod_targets.daily_max);
      response.data.seed_usage_unit && self.setSeedUsageUnit(response.data.seed_usage_unit);
      return response;
    });
  },
  bulkImportFields(features, validate = false) {
    return callAgroApi<{
      message: string;
      imported: number;
    }>(`/organizations/${self.id}/fields/bulk_import${validate ? '?only_validate=true' : ''}`, 'POST', {
      geojson: {
        type: 'FeatureCollection',
        name: 'bulk import',
        features: features
      }
    });
  },
  fetchBoards() {
    return callAgroApi<BoardsResponse[]>(`/organizations/${self.id}/boards`, 'GET');
  },
  fetchBoard({
    id,
    groupBy,
    cropzoneId,
    farmId,
    from,
    to,
    assignee,
    activityTypeId,
    page = 1,
    items
  }: {
    id: string;
    groupBy?: string;
    cropzoneId?: string;
    farmId?: string;
    from?: string;
    to?: string;
    activityTypeId?: string;
    assignee?: string;
    page?: number;
    items?: number;
  }) {
    let queryString = '';
    if (groupBy) {
      queryString += `group_by=${groupBy}&`;
    }
    if (cropzoneId) {
      queryString += `cropzone_id=${cropzoneId}&`;
    }
    if (farmId) {
      queryString += `farm_id=${farmId}&`;
    }
    if (from) {
      queryString += `from=${from}&`;
    }
    if (from && to) {
      queryString += `to=${to}&`;
    }
    if (assignee) {
      queryString += `assignee_id=${assignee}&`;
    }
    if (activityTypeId) {
      queryString += `activity_type_id=${activityTypeId}&`;
    }
    if (items) {
      queryString += `items=${items.toString()}&`;
    }
    return callAgroApi<BoardGroupResponse[]>(`/boards/${id}?${queryString}page=${page.toString()}`, 'GET');
  },
  updateActivityLabel(activityItem: IActivityItem) {
    return callAgroApi<ActivityResponse>(`/organizations/${self.id}/activities/${activityItem.activity_id}`, 'PATCH', {
      labels: [{
        id: activityItem.label_id
      }]
    });
  },
  createActivity(activityForm: {
    cropzone_id: string;
    activity_type_id: string;
    start_date: string;
    end_date: string | null;
    note?: string;
    labels?: {
      id: string;
    }[];
    assignees?: {
      id: string;
    }[];
    toolType?: string;
    toolName?: string;
    extra?: {
      [k: string]: any;
    };
  }) {
    return callAgroApi<ActivityResponse>(`/organizations/${self.id}/activities`, 'POST', removeEmptyExtra(activityForm)).then(({
      data
    }) => ({
      ...Transformers.activity(data),
      activityType: data.activity_type.id,
      cropzone: data.cropzone?.id
    })).then(activity => self.pushToActivities([activity])[0]);
  },
  fetchOrgUsers({
    page = 1,
    status,
    query
  }: {
    page?: number;
    status?: 'active' | 'pending';
    query?: string;
  }) {
    let queryString = '';
    if (status) {
      queryString += `filter[status]=${status}&`;
    }
    if (query) {
      queryString += `filter[query]=${query}`;
    }
    return callAgroApi<UserResponse[]>(`/organizations/${self.id}/users?${queryString}page=${page}`, 'GET');
  },
  fetchOrgUser(id: string) {
    return callAgroApi<UserResponse>(`/users/${id}`, 'GET');
  },
  fetchActivity(id: string) {
    return callAgroApi<ActivityResponse>(`/organizations/${self.id}/activities/${id}`, 'GET').then(({
      data
    }) => Transformers.activity(data)).then(activityCropzoneObject => self.storeCropzonesFromActivities([activityCropzoneObject]).then(res => res[0]));
  },
  updateActivity(id: string, activityForm: {
    cropzone_id;
    activity_type_id: string;
    start_date: string;
    end_date: string | null;
    note?: string;
    labels: {
      id: string;
    }[];
    assignees: {
      id: string;
    }[];
    toolType?: string;
    toolName?: string;
    extra?: object;
  }) {
    return callAgroApi<ActivityResponse>(`/organizations/${self.id}/activities/${id}`, 'PATCH', removeEmptyExtra(activityForm)).then(({
      data
    }) => Transformers.activity(data)).then(activityCropzoneObject => {
      self.storeCropzonesFromActivities([activityCropzoneObject]);
      return self.activities.get(id);
    });
  },
  fetchSankeyData({
    season,
    year = DateTime.now().year,
    crop,
    district,
    country,
    region,
    village,
    subdistrict,
    labels
  }: {
    season?: string;
    year?: number;
    crop?: string;
    labels?: string;
    country?: string;
    district?: string;
    region?: string;
    village?: string;
    subdistrict?: string;
  }) {
    let queryString = '';
    const labelParams = labels?.split(',').map(labelId => `filter[labels][]=${labelId}`);
    if (season) {
      queryString = [`tz=${DateTime.local().zoneName}`, season ? `filter[season_id]=${season}` : null, crop ? `crop=${crop}` : null, country ? `filter[country]=${country}` : null, district ? `filter[district]=${district}` : null, region ? `filter[region]=${region}` : null, village ? `filter[village]=${village}` : null, subdistrict ? `filter[subdistrict]=${subdistrict}` : null, ...(labelParams || [])].filter(Boolean).join('&');
    } else {
      const dates = self.formatQueryDates(year);
      queryString = [`tz=${DateTime.local().zoneName}`, dates.from ? `from=${dates.from}` : null, dates.to ? `to=${dates.to}` : null, crop ? `crop=${crop}` : null, country ? `filter[country]=${country}` : null, district ? `filter[district]=${district}` : null, region ? `filter[region]=${region}` : null, village ? `filter[village]=${village}` : null, subdistrict ? `filter[subdistrict]=${subdistrict}` : null, ...(labelParams || [])].filter(Boolean).join('&');
    }
    return callAgroApi<SankeyResponse>(`/organizations/${self.id}/stats/txes/sankey?${queryString}`, 'GET').then(({
      data
    }) => data);
  },
  fetchTraceabilityChartData({
    season,
    year = DateTime.now().year,
    crop,
    country,
    region,
    district,
    village,
    subdistrict,
    labels
  }: {
    season?: string;
    year?: number;
    crop?: string;
    country?: string;
    district?: string;
    region?: string;
    village?: string;
    subdistrict?: string;
    labels?: string;
  }) {
    let queryString = '';
    const labelParams = labels?.split(',').map(labelId => `filter[labels][]=${labelId}`);
    if (season) {
      queryString = [`tz=${DateTime.local().zoneName}`, season ? `filter[season_id]=${season}` : null, crop ? `crop=${crop}` : null, country ? `filter[country]=${country}` : null, district ? `filter[district]=${district}` : null, region ? `filter[region]=${region}` : null, village ? `filter[village]=${village}` : null, subdistrict ? `filter[subdistrict]=${subdistrict}` : null, ...(labelParams || [])].filter(Boolean).join('&');
    } else {
      const dates = self.formatQueryDates(year);
      queryString = [`tz=${DateTime.local().zoneName}`, dates.from ? `from=${dates.from}` : null, dates.to ? `to=${dates.to}` : null, crop ? `crop=${crop}` : null, country ? `filter[country]=${country}` : null, district ? `filter[district]=${district}` : null, region ? `filter[region]=${region}` : null, village ? `filter[village]=${village}` : null, subdistrict ? `filter[subdistrict]=${subdistrict}` : null, ...(labelParams || [])].filter(Boolean).join('&');
    }
    return callAgroApi<StatResponse>(`/organizations/${self.id}/stats/txes/charts?${queryString}`, 'GET').then(({
      data
    }) => data);
  },
  fetchTraceabilityTransactions({
    page = 1,
    timezone,
    season,
    year
  }: {
    page?: number;
    timezone: string;
    season?: string;
    year?: number;
  }) {
    let queryString = '';
    if (season) {
      queryString = [season ? `filter[season_id]=${season}` : null].filter(Boolean).join('&');
    } else {
      const dates = self.formatQueryDates(year ?? DateTime.now().year);
      queryString = [dates.from ? `filter[from]=${dates.from}` : null, dates.to ? `filter[to]=${dates.to}` : null].filter(Boolean).join('&');
    }
    return callAgroApi<TransactionResponse[]>(`/organizations/${self.id}/txes?page=${page.toString()}&${queryString}`, 'GET').then(({
      data
    }) => data.map(d => Transformers.tx(d, timezone)));
  },
  fetchTraceabilityTransaction({
    transactionId,
    timezone
  }: {
    transactionId: string;
    timezone: string;
  }): Promise<ITransactions> {
    return callAgroApi<TransactionResponse>(`/txes/${transactionId}`, 'GET').then(({
      data
    }) => {
      const transformed = Transformers.tx(data, timezone);
      return {
        ...transformed,
        source: transformed.source ?? undefined
      };
    });
  },
  importFromXlsx(file: File, onStatus?: (status: OrganizationImportStatus) => void): Promise<OrganizationImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const orgId = self.id;
    const archive = async (result: OrganizationImportResponse) => {
      if (self.hasSupplyChainFeature && result.status !== 'failed') {
        await autoArchiveAllForVsc(orgId).catch(() => undefined);
      }
      return result;
    };
    const poll = (importId: string, failures = 0): Promise<OrganizationImportResponse> => fetchOrganizationImport(orgId, importId).then(result => {
      onStatus?.(result.status);
      if (IMPORT_TERMINAL_STATUSES.includes(result.status)) return result;
      return wait(IMPORT_POLL_INTERVAL_MS).then(() => poll(importId));
    }, error => {
      if (!isTransientError(error)) throw error;
      if (failures + 1 >= IMPORT_POLL_MAX_FAILURES) {
        throw importStillProcessingError(importId);
      }
      return wait(importPollDelay(failures + 1)).then(() => poll(importId, failures + 1));
    });
    let bodySent = false;
    return callAgroApi<OrganizationImportQueuedResponse | SyncImportResponse>(`/organizations/${orgId}/imports/xlsx`, 'POST', formData, {
      onUploadProgress: ({
        loaded,
        total
      }) => {
        bodySent = bodySent || (total ? loaded >= total : false);
      }
    }).then(({
      data,
      status
    }) => {
      if (status === 202 || 'import_id' in data) {
        const queued = data as OrganizationImportQueuedResponse;
        onStatus?.(queued.status);
        return poll(queued.import_id).then(archive);
      }
      return archive(syncImportToResponse(data as SyncImportResponse, file));
    }, error => {
      if (bodySent && !error?.response) {
        throw importStillProcessingError(null);
      }
      throw error;
    });
  },
  fetchPlantingStats({
    season,
    year = DateTime.now().year,
    crop,
    country,
    district,
    region,
    village,
    subdistrict,
    labels
  }: {
    season?: string;
    year?: number;
    crop?: string;
    labels?: string;
    country?: string;
    district?: string;
    region?: string;
    village?: string;
    subdistrict?: string;
  }) {
    let queryString = '';
    const labelParams = labels?.split(',').map(labelId => `filter[labels][]=${labelId}`);
    if (season) {
      queryString = [`tz=${DateTime.local().zoneName}`, season ? `filter[season_id]=${season}` : null, crop ? `crop=${crop}` : null, country ? `filter[country]=${country}` : null, district ? `filter[district]=${district}` : null, region ? `filter[region]=${region}` : null, village ? `filter[village]=${village}` : null, subdistrict ? `filter[subdistrict]=${subdistrict}` : null, ...(labelParams || [])].filter(Boolean).join('&');
    } else {
      const dates = self.formatQueryDates(year);
      queryString = [`tz=${DateTime.local().zoneName}`, dates.from ? `from=${dates.from}` : null, dates.to ? `to=${dates.to}` : null, crop ? `crop=${crop}` : null, country ? `filter[country]=${country}` : null, district ? `filter[district]=${district}` : null, region ? `filter[region]=${region}` : null, village ? `filter[village]=${village}` : null, subdistrict ? `filter[subdistrict]=${subdistrict}` : null, ...(labelParams || [])].filter(Boolean).join('&');
    }
    return callAgroApi<PlantingResponse>(`/organizations/${self.id}/stats/planting?${queryString}`, 'GET').then(({
      data
    }) => data);
  }
})).extend(withNotifications).extend(withLabels).extend(withPlaces).extend(withSeasons).extend(withActivities).extend(withTransactions).extend(withAssignments).extend(withTemplates).extend(withSupplyChain);
export type IOrganization = Instance<typeof Organization>;
export type IOrganizationSnapshotIn = SnapshotIn<typeof Organization>;